import { useEffect, useMemo, useState } from 'react';
import { Modal, Tabs, Stack, Table, Text, Group, Button, Badge, Paper, Loader, Center, Progress, SimpleGrid } from '@mantine/core';
import { IconClock, IconActivity, IconDatabase, IconRefresh } from '@tabler/icons-react';
import { api } from '../../api/client';

interface PerfEntry {
  method: string; path: string; durationMs: number; statusCode: number; at: string;
}

interface Metrics {
  counts: Record<string, number>;
  database: { path: string; bytes: number };
  images: { path: string; bytes: number; count: number };
  disk: { freeBytes: number | null; totalBytes: number | null };
  uptimeSec: number;
  node: string;
  serverStartedAt: string | null;
}

function fmtBytes(n: number | null): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 2)} ${units[i]}`;
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  return `${(sec / 3600).toFixed(1)} h`;
}

// --------- Time Performance ---------

function PerfTab() {
  const [entries, setEntries] = useState<PerfEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<{ key: keyof PerfEntry; dir: 'asc' | 'desc' }>({ key: 'durationMs', dir: 'desc' });

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get<{ entries: PerfEntry[] }>('/debug/performance');
      setEntries(r.entries);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const sorted = useMemo(() => {
    const arr = [...entries];
    arr.sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      if (va === vb) return 0;
      const cmp = va < vb ? -1 : 1;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [entries, sort]);

  const toggle = (key: keyof PerfEntry) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  const arrow = (key: keyof PerfEntry) => sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';

  if (loading) return <Center p="xl"><Loader /></Center>;

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">Backend operations ≥ 5 ms (most recent 200). Resets on server restart.</Text>
        <Button size="xs" leftSection={<IconRefresh size={12} />} variant="light" onClick={refresh}>Refresh</Button>
      </Group>
      <Paper withBorder>
        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggle('at')}>Time{arrow('at')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggle('method')}>Method{arrow('method')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggle('path')}>Operation{arrow('path')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggle('durationMs')}>Duration (ms){arrow('durationMs')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggle('statusCode')}>Status{arrow('statusCode')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((e, i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="xs" c="dimmed">{new Date(e.at).toLocaleTimeString()}</Text></Table.Td>
                <Table.Td><Text size="sm" className="cnc-mono">{e.method}</Text></Table.Td>
                <Table.Td><Text size="sm" className="cnc-mono">{e.path}</Text></Table.Td>
                <Table.Td>
                  <Text fw={e.durationMs > 100 ? 600 : 400} c={e.durationMs > 250 ? 'red' : e.durationMs > 100 ? 'orange' : undefined}>
                    {e.durationMs.toFixed(1)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={e.statusCode >= 500 ? 'red' : e.statusCode >= 400 ? 'yellow' : 'green'} variant="light">{e.statusCode}</Badge>
                </Table.Td>
              </Table.Tr>
            ))}
            {sorted.length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text ta="center" size="sm" c="dimmed" py="md">No slow operations recorded yet — use the app for a bit and refresh.</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

// --------- Connection Speed ---------

interface SpeedResult {
  pingMs: number | null;
  downloadMbps: number | null;
  uploadMbps: number | null;
  progress: number;
  phase: 'idle' | 'ping' | 'download' | 'upload' | 'done';
}

function SpeedTab() {
  const [state, setState] = useState<SpeedResult>({ pingMs: null, downloadMbps: null, uploadMbps: null, progress: 0, phase: 'idle' });
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    setState({ pingMs: null, downloadMbps: null, uploadMbps: null, progress: 0, phase: 'ping' });

    // Ping — median of 5
    const pings: number[] = [];
    for (let i = 0; i < 5; i++) {
      const t0 = performance.now();
      try { await api.get('/debug/ping'); } catch { /* ignore */ }
      pings.push(performance.now() - t0);
    }
    pings.sort((a, b) => a - b);
    const pingMs = pings[Math.floor(pings.length / 2)];
    setState((s) => ({ ...s, pingMs, phase: 'download', progress: 0.2 }));

    // Download — 5 MB
    const bytes = 5 * 1024 * 1024;
    const t0d = performance.now();
    const resD = await fetch(`/api/v1/debug/speedtest/download?bytes=${bytes}`, { credentials: 'include', cache: 'no-store' });
    await resD.arrayBuffer();
    const dtD = (performance.now() - t0d) / 1000;
    const downloadMbps = dtD > 0 ? (bytes * 8) / 1e6 / dtD : null;
    setState((s) => ({ ...s, downloadMbps, phase: 'upload', progress: 0.6 }));

    // Upload — 2 MB blob
    const upBytes = 2 * 1024 * 1024;
    const upPayload = new Uint8Array(upBytes);
    const t0u = performance.now();
    await fetch('/api/v1/debug/speedtest/upload', { method: 'POST', credentials: 'include', body: upPayload });
    const dtU = (performance.now() - t0u) / 1000;
    const uploadMbps = dtU > 0 ? (upBytes * 8) / 1e6 / dtU : null;

    setState({ pingMs, downloadMbps, uploadMbps, phase: 'done', progress: 1 });
    setBusy(false);
  }

  const phaseLabel: Record<SpeedResult['phase'], string> = {
    idle: 'Ready', ping: 'Measuring latency…', download: 'Measuring download…', upload: 'Measuring upload…', done: 'Done',
  };

  return (
    <Stack>
      <Text size="sm" c="dimmed">Measures the connection between this browser and the CYCLOTRON server on the local network.</Text>
      <SimpleGrid cols={3}>
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">Latency (ping)</Text>
          <Text fw={700} size="xl" c="orange">{state.pingMs != null ? `${state.pingMs.toFixed(1)} ms` : '—'}</Text>
        </Paper>
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">Download</Text>
          <Text fw={700} size="xl" c="orange">{state.downloadMbps != null ? `${state.downloadMbps.toFixed(1)} Mbps` : '—'}</Text>
        </Paper>
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">Upload</Text>
          <Text fw={700} size="xl" c="orange">{state.uploadMbps != null ? `${state.uploadMbps.toFixed(1)} Mbps` : '—'}</Text>
        </Paper>
      </SimpleGrid>
      {busy && <Progress value={state.progress * 100} color="orange" striped animated />}
      <Group>
        <Button onClick={run} loading={busy}>Run speed test</Button>
        <Text size="sm" c="dimmed">{phaseLabel[state.phase]}</Text>
      </Group>
    </Stack>
  );
}

// --------- Backend Metrics ---------

function MetricsTab() {
  const [m, setM] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try { setM(await api.get<Metrics>('/debug/metrics')); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  if (loading || !m) return <Center p="xl"><Loader /></Center>;

  const usedPct = m.disk.freeBytes != null && m.disk.totalBytes ? 1 - m.disk.freeBytes / m.disk.totalBytes : null;

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">Snapshot taken just now. Refresh to re-query.</Text>
        <Button size="xs" leftSection={<IconRefresh size={12} />} variant="light" onClick={refresh}>Refresh</Button>
      </Group>

      <div className="section">
        <div className="section-header">Record counts</div>
        <div className="section-body">
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {Object.entries(m.counts).map(([k, v]) => (
              <Paper key={k} withBorder p="sm" ta="center">
                <Text size="xs" c="dimmed" tt="capitalize">{k}</Text>
                <Text fw={700} size="xl">{v}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Storage</div>
        <div className="section-body">
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Database</Text>
              <Text fw={700}>{fmtBytes(m.database.bytes)}</Text>
              <Text size="xs" c="dimmed" truncate>{m.database.path}</Text>
            </Paper>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Images ({m.images.count})</Text>
              <Text fw={700}>{fmtBytes(m.images.bytes)}</Text>
              <Text size="xs" c="dimmed" truncate>{m.images.path}</Text>
            </Paper>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Disk free / total</Text>
              <Text fw={700}>{fmtBytes(m.disk.freeBytes)} / {fmtBytes(m.disk.totalBytes)}</Text>
              {usedPct != null && <Progress mt={6} value={usedPct * 100} color={usedPct > 0.9 ? 'red' : usedPct > 0.75 ? 'yellow' : 'green'} />}
            </Paper>
          </SimpleGrid>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Runtime</div>
        <div className="section-body">
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Node version</Text>
              <Text fw={700} className="cnc-mono">{m.node}</Text>
            </Paper>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Uptime</Text>
              <Text fw={700}>{fmtDuration(m.uptimeSec)}</Text>
            </Paper>
            <Paper withBorder p="sm">
              <Text size="xs" c="dimmed">Started at</Text>
              <Text fw={700} size="sm">{m.serverStartedAt ? new Date(m.serverStartedAt).toLocaleString() : '—'}</Text>
            </Paper>
          </SimpleGrid>
        </div>
      </div>
    </Stack>
  );
}

export default function DebugModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal opened onClose={onClose} title={<span className="modal-title-custom">Debug Console</span>} size="xl">
      <Tabs defaultValue="perf" color="orange">
        <Tabs.List>
          <Tabs.Tab value="perf"    leftSection={<IconClock size={14} />}>Time Performance</Tabs.Tab>
          <Tabs.Tab value="speed"   leftSection={<IconActivity size={14} />}>Connection Speed</Tabs.Tab>
          <Tabs.Tab value="metrics" leftSection={<IconDatabase size={14} />}>Backend Metrics</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="perf"    pt="md"><PerfTab /></Tabs.Panel>
        <Tabs.Panel value="speed"   pt="md"><SpeedTab /></Tabs.Panel>
        <Tabs.Panel value="metrics" pt="md"><MetricsTab /></Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
